/**
 * RunSparklineRow — one-line run row for the run-fidelity view (5B).
 *
 * Props in, JSX out. No fetch, no polling, no global state reads. The polling
 * container owns the data and passes it down as snapshots.
 *
 * Renders per run (recency-sorted by the parent):
 *   [run-id mono] [name if distinct] [throughput sparkline]
 *   [done/total] [✓pass ✗fail] [state badge] [timing]
 *
 * Key decisions:
 *   - Name is shown ONLY when run.name !== run.runId (kills the doubled handle).
 *   - State badge: Done (terminal), Stuck (run.stalled), Running (live).
 *   - Sparkline degrades to a flat baseline when throughputSeries is empty.
 *   - Color dot removed: at run granularity each row IS one run, so a hue
 *     carries no grouping signal and loops after 7 runs (false impression).
 */

import { CancelRunButton } from './CancelRunButton'
import type { CancelRunResult } from './board-client'
import { runRowTiming } from './format'
import { hasOutstandingWork } from './pipeline-data'
import type { RunOverviewEntry } from './types'

// ─── Sparkline (inline SVG) ───────────────────────────────────────────────────

const SPARK_W = 60
const SPARK_H = 20
const SPARK_VIEWBOX = `0 0 ${SPARK_W} ${SPARK_H}`
const SPARK_PAD = 2 // px clearance so the polyline never clips the top/bottom edge

interface SparklineProps {
  series: number[]
}

function Sparkline({ series }: SparklineProps): JSX.Element {
  if (series.length === 0) {
    // Flat baseline — not an error, just no trend data yet.
    return (
      <svg className="sparkline" viewBox={SPARK_VIEWBOX} aria-hidden="true" data-testid="sparkline">
        <line
          x1="0"
          y1={SPARK_H / 2}
          x2={SPARK_W}
          y2={SPARK_H / 2}
          className="sparkline-baseline"
          data-testid="sparkline-flat"
        />
      </svg>
    )
  }

  const max = Math.max(...series, 1)
  const points = series
    .map((v, i) => {
      const x = series.length === 1 ? SPARK_W / 2 : (i / (series.length - 1)) * SPARK_W
      // Invert Y: high values should appear near the top of the SVG.
      const y = SPARK_H - SPARK_PAD - (v / max) * (SPARK_H - SPARK_PAD * 2)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <svg className="sparkline" viewBox={SPARK_VIEWBOX} aria-hidden="true" data-testid="sparkline">
      <polyline points={points} className="sparkline-line" data-testid="sparkline-line" />
    </svg>
  )
}

// ─── State badge ──────────────────────────────────────────────────────────────

type BadgeState = 'Done' | 'Stuck' | 'Running'

function runState(run: RunOverviewEntry): BadgeState {
  if (run.stalled) return 'Stuck'
  if (run.total > 0 && run.done === run.total) return 'Done'
  return 'Running'
}

const BADGE_CLASS: Record<BadgeState, string> = {
  Done: 'run-badge run-badge-done',
  Stuck: 'run-badge run-badge-stuck',
  Running: 'run-badge run-badge-running',
}

// ─── RunSparklineRow ──────────────────────────────────────────────────────────

export interface RunSparklineRowProps {
  /** Full run snapshot from GET /runs. */
  run: RunOverviewEntry
  /**
   * Per-minute completion counts for the trailing sparkline. Pass [] when no
   * history is available yet — the sparkline renders a flat baseline, not an
   * error.
   */
  throughputSeries: number[]
  /**
   * Fires a run cancel (`POST /runs/<id>/cancel`). When provided AND the run
   * still has outstanding work, the row shows a Cancel control; omit it (or pass
   * a terminal run) and no control renders. Injected so the row stays
   * presentational — the App binds its `fetchImpl` around the client `cancelRun`.
   */
  onCancelRun?: (runId: string) => Promise<CancelRunResult>
  /** Called once a cancel succeeds, so the parent can re-fetch the board. */
  onRunCancelled?: () => void
}

export function RunSparklineRow({
  run,
  throughputSeries,
  onCancelRun,
  onRunCancelled,
}: RunSparklineRowProps): JSX.Element {
  const state = runState(run)
  // Show the human name only when it differs from the raw run-id.
  const showName = run.name !== run.runId
  const timing = runRowTiming(run)
  // Only a run with work still in a non-terminal lane (queued/leased/running/
  // interrupted/retryable) is cancellable — mirror the server's active/finished
  // split so the control never shows as a primary action on a terminal run.
  const cancellable = onCancelRun !== undefined && hasOutstandingWork(run.counts)

  return (
    <li
      className="run-sparkline-row"
      data-testid="run-sparkline-row"
      data-state={state.toLowerCase()}
      data-stalled={run.stalled}
    >
      {/* Identity: run-id always, human name only when distinct */}
      <span className="run-id tabular" data-testid="run-id">
        {run.runId}
      </span>
      {showName && (
        <span className="run-name" data-testid="run-name">
          {run.name}
        </span>
      )}

      {/* Throughput sparkline — flat baseline when series is empty */}
      <Sparkline series={throughputSeries} />

      {/* Progress: done / total */}
      <span className="run-progress tabular" data-testid="run-progress">
        {run.done}/{run.total}
      </span>

      {/* Pass / fail split */}
      <span className="run-passfail">
        <span className="run-pass tabular" data-testid="run-pass">
          ✓{run.passCount}
        </span>
        <span className="run-fail tabular" data-testid="run-fail">
          ✗{run.failCount}
        </span>
      </span>

      {/* State badge: Done | Stuck | Running — visually distinct */}
      <span className={BADGE_CLASS[state]} data-testid="run-state-badge">
        {state}
      </span>

      {/* Human-readable timing: when the run was first seen + how long it has spanned. */}
      <span className="run-timing tabular" data-testid="run-timing" title={timing.whenIso}>
        <span className="run-when">{timing.whenClock}</span>
        <span className="run-timing-sep" aria-hidden="true">
          ·
        </span>
        <span className="run-duration">{timing.duration}</span>
      </span>

      {/* Run-level cancel — only for a live run, and only when the parent wires it. */}
      {cancellable ? <CancelRunButton runId={run.runId} cancel={onCancelRun} onCancelled={onRunCancelled} /> : null}
    </li>
  )
}
